/**
 * Centralized TanStack Query keys. Always prefer the helpers here over inline
 * arrays so we can invalidate cleanly from anywhere.
 */
export const queryKeys = {
  auth: {
    me: () => ['auth', 'me'] as const,
  },
  users: {
    all: () => ['users'] as const,
    list: (params: { search?: string; page?: number; pageSize?: number }) =>
      ['users', 'list', params] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },
  settings: {
    app: () => ['settings', 'app'] as const,
    openaiHealth: () => ['settings', 'openai-health'] as const,
    googleDriveHealth: () => ['settings', 'google-drive-health'] as const,
    bufferHealth: () => ['settings', 'buffer-health'] as const,
    r2Health: () => ['settings', 'r2-health'] as const,
  },
  agents: {
    all: () => ['agents'] as const,
    list: () => ['agents', 'list'] as const,
    detail: (id: string) => ['agents', 'detail', id] as const,
    users: (agentId: string) => ['agents', agentId, 'users'] as const,
  },
  userAgents: {
    list: (userId: string) => ['users', userId, 'agents'] as const,
  },
  sheets: {
    all: () => ['sheets'] as const,
    list: () => ['sheets', 'list'] as const,
    detail: (id: string) => ['sheets', 'detail', id] as const,
    users: (sheetId: string) => ['sheets', sheetId, 'users'] as const,
    data: (sheetId: string, tab?: string | null) =>
      ['sheets', sheetId, 'data', tab ?? null] as const,
  },
  userSheets: {
    list: (userId: string) => ['users', userId, 'sheets'] as const,
  },
  folders: {
    all: () => ['folders'] as const,
    list: () => ['folders', 'list'] as const,
    detail: (id: string) => ['folders', 'detail', id] as const,
    users: (folderId: string) => ['folders', folderId, 'users'] as const,
    contents: (id: string) => ['folders', id, 'contents'] as const,
  },
  userFolders: {
    list: (userId: string) => ['users', userId, 'folders'] as const,
  },
  conversations: {
    all: () => ['conversations'] as const,
    list: () => ['conversations', 'list'] as const,
    detail: (id: string) => ['conversations', 'detail', id] as const,
    messages: (id: string) => ['conversations', id, 'messages'] as const,
  },
  mediaCalendar: {
    all: () => ['mediaCalendar'] as const,
    profiles: () => ['mediaCalendar', 'profiles'] as const,
    posts: (params: { from?: string; to?: string; profileId?: string | null }) =>
      ['mediaCalendar', 'posts', params] as const,
    post: (id: string) => ['mediaCalendar', 'post', id] as const,
    grants: () => ['mediaCalendar', 'grants'] as const,
    access: (userId: string) => ['mediaCalendar', 'access', userId] as const,
  },
  support: {
    all: () => ['support'] as const,
    access: (userId: string) => ['support', 'access', userId] as const,
    aiConfig: () => ['support', 'ai-config'] as const,
    channels: () => ['support', 'channels'] as const,
    kbItems: (
      ownerKind: 'support_ai' | 'agent' | 'content_calendar' | 'crm_sales' | 'agent_profile',
      ownerId: string,
    ) => ['support', 'kb-items', ownerKind, ownerId] as const,
    kbItem: (id: string) => ['support', 'kb-item', id] as const,
    grants: () => ['support', 'grants'] as const,
    // Ticket board (UST-3127). `ticketsAll` is the prefix every filtered board
    // query shares, so one invalidation refreshes them all.
    ticketsAll: () => ['support', 'tickets'] as const,
    tickets: (filters: object) => ['support', 'tickets', filters] as const,
    ticketAssignees: () => ['support', 'ticket-assignees'] as const,
    // UST-3143 — Support Archive. `archiveAll` prefixes both lists.
    archiveAll: () => ['support', 'archive'] as const,
    archiveList: (kind: 'archived' | 'deleted', search: string) =>
      ['support', 'archive', kind, search] as const,
    deletedTicket: (id: string) => ['support', 'archive', 'deleted-ticket', id] as const,
    // UST-3143 — whether the Odoo Helpdesk tab exists, and its teams' stages.
    odooHelpdesk: () => ['support', 'odoo-helpdesk'] as const,
    // Support workflow & policies — the Priority policy's priorities.
    priorities: () => ['support', 'priorities'] as const,
    // Support workflow & policies — the Workflow's stages.
    stages: () => ['support', 'stages'] as const,
    // Support workflow & policies — custom ticket fields, and what a
    // ticket's stage needs of it.
    customFields: () => ['support', 'custom-fields'] as const,
    // Support Contacts — the register, one contact, and a conversation's contact.
    customersAll: () => ['support', 'customers'] as const,
    customers: (params: object) => ['support', 'customers', 'list', params] as const,
    customer: (id: string) => ['support', 'customers', 'one', id] as const,
    conversationCustomer: (conversationId: string) =>
      ['support', 'customers', 'conversation', conversationId] as const,
    // Support workflow & policies — the stages' automations, and a ticket's runs.
    automations: () => ['support', 'automations'] as const,
    ticketAutomationRuns: (ticketId: string) =>
      ['support', 'ticket-automation-runs', ticketId] as const,
    ticketStageHistory: (ticketId: string) =>
      ['support', 'ticket-stage-history', ticketId] as const,
    // Support workflow & policies — each contract type's multi-ticket policy.
    multiTicketPolicies: () => ['support', 'multi-ticket-policies'] as const,
    ticketRequirements: (ticketId: string) =>
      ['support', 'ticket-requirements', ticketId] as const,
  },
  // UST-3159 — Support Contracts.
  supportContracts: {
    all: () => ['supportContracts'] as const,
    myAccess: (userId: string) => ['supportContracts', 'my-access', userId] as const,
    grants: () => ['supportContracts', 'grants'] as const,
    settings: () => ['supportContracts', 'settings'] as const,
    list: () => ['supportContracts', 'list'] as const,
    types: () => ['supportContracts', 'types'] as const,
    accountManagers: () => ['supportContracts', 'account-managers'] as const,
    serviceTypes: () => ['supportContracts', 'service-types'] as const,
    timeLogs: (contractId: string) => ['supportContracts', 'time-logs', contractId] as const,
    ticketUses: (contractId: string) => ['supportContracts', 'ticket-uses', contractId] as const,
    // UST-3159 step 4 — the contract panel inside a ticket.
    ticketTimeAll: (ticketId: string) => ['supportContracts', 'ticket-time', ticketId] as const,
    ticketTime: (ticketId: string, contractId: string | null) =>
      ['supportContracts', 'ticket-time', ticketId, contractId] as const,
  },
  // Support Monitor — access for the page (Phase 7) and the Settings grants.
  supportMonitor: {
    access: (userId: string) => ['supportMonitor', 'access', userId] as const,
    grants: () => ['supportMonitor', 'grants'] as const,
    kpis: (filters: object) => ['supportMonitor', 'kpis', filters] as const,
  },
  // UST-3132 — Telephony Support (inbound calls).
  telephony: {
    all: () => ['telephony'] as const,
    config: () => ['telephony', 'config'] as const,
    callsAll: () => ['telephony', 'calls'] as const,
    calls: (filters: object) => ['telephony', 'calls', filters] as const,
    call: (id: string) => ['telephony', 'call', id] as const,
  },
  contentCalendar: {
    all: () => ['contentCalendar'] as const,
    access: (userId: string) => ['contentCalendar', 'access', userId] as const,
    config: () => ['contentCalendar', 'config'] as const,
    campaigns: () => ['contentCalendar', 'campaigns'] as const,
    campaign: (id: string) => ['contentCalendar', 'campaign', id] as const,
    posts: (params: { from?: string; to?: string }) =>
      ['contentCalendar', 'posts', params] as const,
    post: (id: string) => ['contentCalendar', 'post', id] as const,
    grants: () => ['contentCalendar', 'grants'] as const,
  },
  visualsGenerator: {
    all: () => ['visualsGenerator'] as const,
    access: (userId: string) => ['visualsGenerator', 'access', userId] as const,
    catalog: () => ['visualsGenerator', 'catalog'] as const,
    generations: () => ['visualsGenerator', 'generations'] as const,
    generation: (id: string) => ['visualsGenerator', 'generation', id] as const,
    grants: () => ['visualsGenerator', 'grants'] as const,
    promptTemplates: () => ['visualsGenerator', 'promptTemplates'] as const,
    promptAiConfig: () => ['visualsGenerator', 'promptAiConfig'] as const,
  },
  templateFill: {
    all: () => ['templateFill'] as const,
    access: (userId: string) => ['templateFill', 'access', userId] as const,
    catalog: () => ['templateFill', 'catalog'] as const,
    templates: (q?: string | undefined) => ['templateFill', 'templates', q ?? ''] as const,
    layers: (templateId: string) => ['templateFill', 'layers', templateId] as const,
    generations: () => ['templateFill', 'generations'] as const,
    generation: (id: string) => ['templateFill', 'generation', id] as const,
    grants: () => ['templateFill', 'grants'] as const,
  },
  videoEditor: {
    all: () => ['videoEditor'] as const,
    access: (userId: string) => ['videoEditor', 'access', userId] as const,
    catalog: () => ['videoEditor', 'catalog'] as const,
    jobs: () => ['videoEditor', 'jobs'] as const,
    job: (id: string) => ['videoEditor', 'job', id] as const,
    grants: () => ['videoEditor', 'grants'] as const,
  },
  crm: {
    all: () => ['crm'] as const,
    access: (userId: string) => ['crm', 'access', userId] as const,
    contacts: (search?: string | undefined) => ['crm', 'contacts', search ?? null] as const,
    contact: (id: string) => ['crm', 'contact', id] as const,
    state: () => ['crm', 'state'] as const,
    grants: () => ['crm', 'grants'] as const,
    // v2.3 Command Center
    stats: (from?: string | undefined, to?: string | undefined) =>
      ['crm', 'stats', from ?? null, to ?? null] as const,
    calendar: (from?: string | undefined, to?: string | undefined) =>
      ['crm', 'calendar', from ?? null, to ?? null] as const,
    timeline: (id: string) => ['crm', 'timeline', id] as const,
    profile: (id: string) => ['crm', 'profile', id] as const,
    voiceBriefing: (id: string) => ['crm', 'voice-briefing', id] as const,
    // v2.8 sales pipeline
    leads: () => ['crm', 'leads'] as const,
    leadsPriority: () => ['crm', 'leads', 'priority'] as const,
    lead: (id: string) => ['crm', 'lead', id] as const,
    contactLead: (contactId: string) => ['crm', 'contact-lead', contactId] as const,
    // v2.8.2 CRM → Mail tab (sales mailbox)
    salesMail: (folder: string, search?: string | undefined) =>
      ['crm', 'sales-mail', folder, search ?? null] as const,
    salesMailMessage: (id: string) => ['crm', 'sales-mail-message', id] as const,
    salesMailDrafts: () => ['crm', 'sales-mail-drafts'] as const,
  },
  mail: {
    all: () => ['mail'] as const,
    directory: () => ['mail', 'directory'] as const,
    inbox: () => ['mail', 'inbox'] as const,
    sent: () => ['mail', 'sent'] as const,
    detail: (id: string) => ['mail', 'detail', id] as const,
  },
  leads: {
    all: () => ['leads'] as const,
    access: (userId: string) => ['leads', 'access', userId] as const,
    list: (view: string) => ['leads', 'list', view] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
    count: () => ['leads', 'count'] as const,
    grants: () => ['leads', 'grants'] as const,
  },
  communication: {
    all: () => ['communication'] as const,
    access: () => ['communication', 'access'] as const,
    channels: () => ['communication', 'channels'] as const,
    adminChannels: () => ['communication', 'adminChannels'] as const,
    channelGrants: (channelId: string) =>
      ['communication', 'channelGrants', channelId] as const,
    conversations: (params: { channelId?: string | null; status?: string | null }) =>
      ['communication', 'conversations', params] as const,
    messages: (conversationId: string) =>
      ['communication', 'messages', conversationId] as const,
    scheduled: (params: { channelId?: string | null; from?: string | null; to?: string | null }) =>
      ['communication', 'scheduled', params] as const,
    recipientLists: () => ['communication', 'recipientLists'] as const,
  },
  seo: {
    all: () => ['seo'] as const,
    access: (userId: string) => ['seo', 'access', userId] as const,
    config: () => ['seo', 'config'] as const,
    grants: () => ['seo', 'grants'] as const,
    analyses: () => ['seo', 'analyses'] as const,
    analysis: (id: string) => ['seo', 'analysis', id] as const,
    suggestions: (filters: Record<string, string | undefined>) =>
      ['seo', 'suggestions', filters] as const,
    suggestionPlaybook: (id: string) =>
      ['seo', 'suggestion', id, 'playbook'] as const,
    usage: () => ['seo', 'usage'] as const,
  },
  backup: {
    all: () => ['backup'] as const,
    config: () => ['backup', 'config'] as const,
    history: (cursor?: string) => ['backup', 'history', cursor ?? 'first'] as const,
    detail: (id: string) => ['backup', 'detail', id] as const,
  },
  scraper: {
    all: () => ['scraper'] as const,
    agentsAdmin: () => ['scraper', 'agents', 'admin'] as const,
    agentsUser: () => ['scraper', 'agents', 'user'] as const,
    agentAccess: (id: string) => ['scraper', 'agents', id, 'access'] as const,
    sessions: (filters?: Record<string, unknown>) =>
      ['scraper', 'sessions', filters ?? {}] as const,
    session: (id: string) => ['scraper', 'sessions', id] as const,
    inbox: () => ['scraper', 'shares', 'inbox'] as const,
    usage: () => ['scraper', 'usage'] as const,
    gmapsKey: () => ['scraper', 'gmaps-key'] as const,
  },
  odoo: {
    config: () => ['odoo', 'config'] as const,
    /** Admin grants list — GET /admin/odoo/access → OdooAccessGrantView[]. */
    access: () => ['odoo', 'access'] as const,
    /** Caller's own module access flag — GET /odoo/access → { access }. Distinct
     *  key from `access()` so the boolean never clobbers the admin grants array. */
    moduleAccess: () => ['odoo', 'moduleAccess'] as const,
    models: (q: string) => ['odoo', 'models', q] as const,
    logs: (filters: Record<string, string | boolean | undefined>) =>
      ['odoo', 'logs', filters] as const,
    agentConfig: () => ['odoo', 'agentConfig'] as const,
    // UST-3143 — Helpdesk sync settings.
    helpdesk: () => ['odoo', 'helpdesk'] as const,
    sessions: () => ['odoo', 'sessions'] as const,
    session: (id: string) => ['odoo', 'session', id] as const,
  },
  automation: {
    /** Caller's own module access — GET /automation/access → { access, automations }. */
    moduleAccess: () => ['automation', 'moduleAccess'] as const,
    /** Caller's usable automation catalog — GET /automation/catalog. */
    catalog: () => ['automation', 'catalog'] as const,
    /** Admin config — GET /admin/automation/config. */
    config: () => ['automation', 'config'] as const,
    /** Admin grants — GET /admin/automation/grants. */
    grants: () => ['automation', 'grants'] as const,
    /** Admin agent profile id for one automation — GET /admin/automation/:key/agent-config. */
    agentConfig: (key: string) => ['automation', 'agentConfig', key] as const,
    sessions: (key: string) => ['automation', key, 'sessions'] as const,
    session: (key: string, id: string) => ['automation', key, 'session', id] as const,
  },
  hr: {
    all: () => ['hr'] as const,
    access: (userId: string) => ['hr', 'access', userId] as const,
    settings: () => ['hr', 'settings'] as const,
    grants: () => ['hr', 'grants'] as const,
    employees: (params: { search?: string | null; status?: string | null; department?: string | null }) =>
      ['hr', 'employees', params] as const,
    employee: (id: string) => ['hr', 'employee', id] as const,
    jobs: () => ['hr', 'jobs'] as const,
    job: (id: string) => ['hr', 'job', id] as const,
    candidates: (params: { jobId?: string | null; stage?: string | null; search?: string | null }) =>
      ['hr', 'candidates', params] as const,
    candidate: (id: string) => ['hr', 'candidate', id] as const,
    candidateInterviews: (candidateId: string) =>
      ['hr', 'candidateInterviews', candidateId] as const,
    candidateOffer: (candidateId: string) => ['hr', 'candidateOffer', candidateId] as const,
    calendar: (params: { from: string; to: string }) => ['hr', 'calendar', params] as const,
    mailMessages: (params: { folder?: string | null; search?: string | null }) =>
      ['hr', 'mailMessages', params] as const,
    mailMessage: (id: string) => ['hr', 'mailMessage', id] as const,
    mailTemplates: () => ['hr', 'mailTemplates'] as const,
    agents: () => ['hr', 'agents'] as const,
    agent: (kind: string) => ['hr', 'agent', kind] as const,
    agentRuns: (kind: string) => ['hr', 'agent', kind, 'runs'] as const,
    cvScoreHistory: () => ['hr', 'agent', 'cv_scorer', 'adhoc-history'] as const,
    documents: (entityKind: string, entityId: string) =>
      ['hr', 'documents', entityKind, entityId] as const,
    policies: () => ['hr', 'policies'] as const,
    onboardingTemplates: () => ['hr', 'onboardingTemplates'] as const,
    onboardingInstances: () => ['hr', 'onboardingInstances'] as const,
    onboardingInstance: (id: string) => ['hr', 'onboardingInstance', id] as const,
    onboardingInstanceTasks: (id: string) =>
      ['hr', 'onboardingInstance', id, 'tasks'] as const,
    reminders: (windowDays: number) => ['hr', 'reminders', windowDays] as const,
  },
  agentProfiles: {
    all: () => ['agentProfiles'] as const,
    list: (filter: { kind?: string | undefined; moduleKey?: string | undefined }) =>
      ['agentProfiles', 'list', filter] as const,
    detail: (id: string) => ['agentProfiles', 'detail', id] as const,
    sessions: (id: string, limit: number) =>
      ['agentProfiles', id, 'sessions', limit] as const,
    skills: (id: string, includeArchived: boolean) =>
      ['agentProfiles', id, 'skills', includeArchived] as const,
    toolsCatalog: () => ['agentProfiles', 'toolsCatalog'] as const,
    memory: (id: string) => ['agentProfiles', id, 'memory'] as const,
  },
  llmProviders: {
    status: () => ['llmProviders', 'status'] as const,
  },
  llmModels: {
    list: (provider: string) => ['llmModels', 'list', provider] as const,
  },
} as const;
